"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import careerJobs from "@/lib/careers";

// 1. Move static data outside to prevent re-creation on every render
const DEPARTMENT_OPTIONS = [
  "Accounting and Bookkeeping",
  "Capital Markets Research",
  "Market Research",
  "Investment & Transaction Advisory",
  "Data Analytics",
];

export default function OpenRoles() {
  const [department, setDepartment] = useState("All");

  // 2. Performance: Memoize the filtered list so it only recalculates 
  // when 'department' or 'careerJobs' actually changes.
  const filteredJobs = useMemo(() => {
    return department === "All" 
      ? careerJobs 
      : careerJobs.filter((job) => job.department === department);
  }, [department]);

  return (
    <section id="jobs" className="bg-[#F8F9FB] py-20" aria-labelledby="jobs-heading">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Header Area */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 px-4 gap-6">
          <h2 id="jobs-heading" className="text-4xl font-extrabold text-[#0F172A]">
            Job Opportunities
          </h2>

          <div className="relative">
            {/* 3. Accessibility: Added a hidden label for screen readers */}
            <label htmlFor="dept-select" className="sr-only">Filter by department</label>
            <select
              id="dept-select"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="appearance-none bg-white border border-gray-200 text-gray-600 py-3 px-6 pr-12 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm cursor-pointer w-full md:w-72"
            >
              <option value="All">All Departments</option>
              {DEPARTMENT_OPTIONS.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" aria-hidden="true">
              <svg className="fill-current h-4 w-4" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
              </svg>
            </div>
          </div>
        </div>

        {/* 4. Accessibility & SEO: Added aria-live to announce updates and used 'role=list' */}
        <div 
          className="space-y-4" 
          role="list" 
          aria-live="polite" 
          aria-relevant="additions removals"
        >
          {filteredJobs.length > 0 ? (
            filteredJobs.map((job) => (
              <article
                key={job.id}
                role="listitem"
                className="bg-white border border-gray-100 rounded-3xl p-8 flex flex-col lg:flex-row lg:items-center justify-between gap-6 hover:shadow-lg transition-shadow duration-300"
              >
                {/* 5. SEO: Using H3/H4 for proper heading nested structure */}
                <div className="lg:w-2/5">
                  <h3 className="text-xl font-bold text-[#0F172A]">
                    {job.title}
                  </h3>
                </div>

                <div className="flex flex-col sm:flex-row flex-1 lg:justify-between items-start sm:items-center gap-4 text-gray-500 font-medium">
                  <span className="lg:w-1/2 text-left">
                    {job.department}
                  </span>
                  
                  <span className="lg:w-1/2 text-left lg:text-right">
                    {job.description.experience || "Full Time"}
                  </span>
                </div>

                <div className="lg:w-1/4 flex lg:justify-end">
                  <Link
                    href={`/career/${job.slug}`}
                    className="w-full lg:w-auto text-center bg-[#4B91F7] hover:bg-blue-600 text-white font-bold py-3 px-10 rounded-xl transition-colors duration-200"
                    aria-label={`Apply for ${job.title}`}
                  >
                    Apply Now
                  </Link>
                </div>
              </article>
            ))
          ) : (
            <div className="text-center py-20 text-gray-500" role="status">
              No open roles found in this department.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}